import { computed, ref } from 'vue'

export function useDateFilter(filterType: string) {
  const localFrom = ref('')
  const localTo = ref('')

  const isDateTime = computed(() => filterType === 'date_time')
  const inputType = computed(() => isDateTime.value ? 'datetime-local' : 'date')

  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    if (isDateTime.value) {
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day}T${hours}:${minutes}`
    }
    return `${year}-${month}-${day}`
  }

  const getStartOfDay = (date: Date) => {
    const start = new Date(date)
    start.setHours(0, 0, 0, 0)
    return start
  }

  const getStartOfWeekMonday = (date: Date) => {
    const d = new Date(date)
    const day = d.getDay()
    const diffToMonday = (day === 0 ? -6 : 1) - day
    d.setDate(d.getDate() + diffToMonday)
    return getStartOfDay(d)
  }

  const addDays = (date: Date, n: number) => {
    const d = new Date(date)
    d.setDate(d.getDate() + n)
    return d
  }

  const getLastDayOfMonth = (year: number, monthIndex: number) => {
    return new Date(year, monthIndex + 1, 0)
  }

  const setDatePreset = (preset: string) => {
    const today = new Date()
    const formatFn = formatDateForInput

    switch (preset) {
      case 'today': {
        const start = getStartOfDay(today)
        localFrom.value = formatFn(start)
        localTo.value = formatFn(start)
        break
      }
      case 'yesterday': {
        const y = addDays(getStartOfDay(today), -1)
        localFrom.value = formatFn(y)
        localTo.value = formatFn(y)
        break
      }
      case 'current_week': {
        const startOfWeek = getStartOfWeekMonday(today)
        const endOfWeek = addDays(startOfWeek, 6)
        localFrom.value = formatFn(startOfWeek)
        localTo.value = formatFn(getStartOfDay(endOfWeek))
        break
      }
      case 'last_week': {
        const startOfThisWeek = getStartOfWeekMonday(today)
        const startOfLastWeek = addDays(startOfThisWeek, -7)
        const endOfLastWeek = addDays(startOfLastWeek, 6)
        localFrom.value = formatFn(startOfLastWeek)
        localTo.value = formatFn(getStartOfDay(endOfLastWeek))
        break
      }
      case 'current_month': {
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
        const lastDay = getLastDayOfMonth(today.getFullYear(), today.getMonth())
        localFrom.value = formatFn(getStartOfDay(startOfMonth))
        localTo.value = formatFn(getStartOfDay(lastDay))
        break
      }
      case 'last_month': {
        const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1)
        const lastDayLastMonth = getLastDayOfMonth(startOfLastMonth.getFullYear(), startOfLastMonth.getMonth())
        localFrom.value = formatFn(getStartOfDay(startOfLastMonth))
        localTo.value = formatFn(getStartOfDay(lastDayLastMonth))
        break
      }
    }
  }

  const initFromModel = (modelValue: unknown) => {
    const o = (modelValue && typeof modelValue === 'object' && 'from' in modelValue)
      ? modelValue as { from?: string; to?: string }
      : null
    localFrom.value = o?.from ?? ''
    localTo.value = o?.to ?? ''
  }

  const getValue = () => {
    return (localFrom.value || localTo.value)
      ? { from: localFrom.value || undefined, to: localTo.value || undefined }
      : null
  }

  return {
    localFrom,
    localTo,
    inputType,
    setDatePreset,
    initFromModel,
    getValue,
  }
}
